import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from './axiosConfig';
import './NewDSIAnalysisReports.css';

const POLL_MS = 60_000; // 60s
const WINDOW_HOURS = 72;

function timeAgo(iso, t) {
  if (!iso) return t('ndsi.unknown');
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return t('ndsi.secondsAgo', { count: sec });
  const min = Math.floor(sec / 60);
  if (min < 60) return t('ndsi.minutesAgo', { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t('ndsi.hoursAgo', { count: hr });
  const day = Math.floor(hr / 24);
  return t('ndsi.daysAgo', { count: day });
}

function extractList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  return [];
}

// Try to recover "First Last" from a filename like "Last_First_1560000002_CON_...pdf"
function deriveNameFromFilename(fn) {
  if (!fn) return null;
  const leaf = String(fn).split('/').pop();
  const base = leaf.replace(/\.[^.]+$/, '');
  const parts = base.split('_');
  if (parts.length >= 2) {
    const last = parts[0].replace(/[^A-Za-z'-]/g, '');
    const first = parts[1].replace(/[^A-Za-z'-]/g, '');
    if (first && last) return `${first} ${last}`;
  }
  return null;
}

function normalizeName(item, filename) {
  const f = (item.patient_first_name || item.first_name || '').trim();
  const l = (item.patient_last_name  || item.last_name  || '').trim();
  if (f || l) return [f, l].filter(Boolean).join(' ').trim();

  const provided = (item.patient_name || '').trim();
  const looksLikeFolderSession = /^[A-Za-z]+[A-Za-z]+-\d{6,}/.test(provided || '');
  if (!provided || looksLikeFolderSession) {
    const recovered = deriveNameFromFilename(filename);
    if (recovered) return recovered;
  }
  return provided || '';
}

function normalize(item) {
  const patient_id = item.patient_id ?? item.patient ?? null;
  const blob_name = item.blob_name || null;
  const filename = item.filename || (blob_name ? blob_name.split('/').pop() : 'report.pdf');
  const patient_name = normalizeName(item, filename);
  const location_name = item.location_name ?? item.center_name ?? item.location ?? null;
  const created_at = item.created_at || item.notified_at || null;
  const acknowledged_at = item.acknowledged_at || null;

  return {
    id: item.report_id ?? item.id ?? blob_name ?? `${patient_id || 'x'}:${filename}`,
    report_id: item.report_id ?? item.id ?? null,
    patient_id,
    patient_name,
    location_name,
    filename,
    blob_name,
    blob_url: item.blob_url || null,
    created_at,
    acknowledged_at,
    is_unread: acknowledged_at ? false : true,
  };
}

export default function NewDSIAnalysisReports() {
  const { t, i18n } = useTranslation();
  const locale = String(i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US';
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [includeReviewed, setIncludeReviewed] = useState(false);

  const pollRef = useRef(null);
  const fetchingRef = useRef(false);
  const controllerRef = useRef(null);
  const navigate = useNavigate();

  const abortInFlight = () => {
    if (controllerRef.current) {
      try {
        controllerRef.current.abort();
      } catch {}
      controllerRef.current = null;
    }
  };

  const fetchReports = useCallback(
    async ({ includeReviewedOverride } = {}) => {
      if (fetchingRef.current) abortInFlight();
      fetchingRef.current = true;
      setError('');
      setLoading(true);

      const controller = new AbortController();
      controllerRef.current = controller;

      try {
        const include = includeReviewedOverride ?? includeReviewed;
        const params = { scope: 'mine', since_hours: WINDOW_HOURS, scan: 1 };
        if (include) params.include_reviewed = 1;

        const res = await axios.get('reports/new-dsi/', {
          params,
          signal: controller.signal,
        });
        const raw = extractList(res.data);
        const normalized = raw.map(normalize);
        const filtered = include ? normalized : normalized.filter((r) => r.is_unread);
        setReports(filtered);
      } catch (e) {
        if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
          console.error('new-dsi fetch failed', e);
          setError(
            t('ndsi.error', {
              defaultValue: 'Unable to load new DSI reports.',
            })
          );
          setReports([]);
        }
      } finally {
        fetchingRef.current = false;
        setLoading(false);
      }
    },
    [includeReviewed, t]
  );

  useEffect(() => {
    fetchReports();

    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => fetchReports(), POLL_MS);

    const onAck = () => fetchReports();
    window.addEventListener('cti:report-acknowledged', onAck);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      window.removeEventListener('cti:report-acknowledged', onAck);
      abortInFlight();
    };
  }, [fetchReports]);

  const onToggleReviewed = (e) => {
    const next = e.target.checked;
    setIncludeReviewed(next);
    fetchReports({ includeReviewedOverride: next });
  };

  // Resolve to a patient id when missing by searching the patients endpoint
  const resolveAndGoToPatient = async (r) => {
    if (r.patient_id) {
      navigate(`/dashboard/patients/${r.patient_id}/view?tab=reports`);
      return;
    }
    const guess =
      r.patient_name
        ? r.patient_name
        : deriveNameFromFilename(r.filename);

    if (!guess) {
      alert(
        t('ndsi.unableLocate', {
          defaultValue: 'Unable to locate patient for this report.',
        })
      );
      return;
    }

    try {
      const resp = await axios.get('patients/', { params: { search: guess } });
      const list = Array.isArray(resp.data?.results)
        ? resp.data.results
        : Array.isArray(resp.data)
        ? resp.data
        : [];
      const found =
        list.find(
          (p) =>
            `${(p.first_name || '').trim()} ${(p.last_name || '').trim()}`.trim().toLowerCase() ===
            guess.trim().toLowerCase()
        ) || list[0];

      if (found?.id) {
        navigate(`/dashboard/patients/${found.id}/view?tab=reports`);
      } else {
        alert(
          t('ndsi.unableLocate', {
            defaultValue: 'Unable to locate patient for this report.',
          })
        );
      }
    } catch {
      alert(
        t('ndsi.unableSearchPatients', {
          defaultValue: 'Unable to search patients to resolve this report.',
        })
      );
    }
  };

  return (
    <div className="ndsi-wrap">
      <div className="ndsi-head">
        <h2 className="ndsi-title">
          {t('ndsi.title', { defaultValue: 'New DSI Analysis Reports' })}
        </h2>
        <button
          className="ndsi-refresh"
          onClick={() => fetchReports()}
          aria-label={t('ndsi.refresh', { defaultValue: 'Refresh list' })}
        >
          {t('ndsi.refresh', { defaultValue: 'Refresh' })}
        </button>
      </div>

      <div className="pr-controls">
        <label>
          <input type="checkbox" checked={includeReviewed} onChange={onToggleReviewed} />{' '}
          {t('ndsi.includeReviewed', { defaultValue: 'Include reviewed' })}
        </label>
      </div>

      {loading && (
        <div className="ndsi-status">
          {t('ndsi.loading', { defaultValue: 'Loading reports...' })}
        </div>
      )}
      {!loading && error && <div className="ndsi-error">{error}</div>}

      {!loading && !error && reports.length === 0 && (
        <div className="ndsi-empty">
          <div className="ndsi-empty-icon">?</div>
          <div>
            {t('ndsi.empty', {
              days: WINDOW_HOURS / 24,
              defaultValue:
                'No reports are available yet. New reports appear here for up to {{days}} days.',
            })}
          </div>
        </div>
      )}

      {!loading && !error && reports.length > 0 && (
        <div className="ndsi-grid">
          {reports.map((r, idx) => (
            <div
              key={r.id || r.blob_name || idx}
              className={`ndsi-card ${
                r.is_unread ? 'ndsi-card--unread' : 'ndsi-card--read'
              }`}
            >
              <div className="ndsi-card-row">
                <span className="ndsi-key">
                  {t('ndsi.patient', { defaultValue: 'Patient' })}:
                </span>
                <span className="ndsi-val">
                  {r.patient_name || t('ndsi.unknown')}
                  {r.is_unread && (
                    <span
                      className="ndsi-badge-unreviewed"
                      title={t('ndsi.notReviewed', { defaultValue: 'Not reviewed' })}
                    >
                      {t('ndsi.unreviewed', { defaultValue: 'unreviewed' })}
                    </span>
                  )}
                </span>
              </div>

              {r.location_name ? (
                <div className="ndsi-card-row">
                  <span className="ndsi-key">
                    {t('ndsi.location', { defaultValue: 'Location' })}:
                  </span>
                  <span className="ndsi-val">{r.location_name}</span>
                </div>
              ) : null}

              <div className="ndsi-card-row">
                <span className="ndsi-key">
                  {t('ndsi.report', { defaultValue: 'Report' })}:
                </span>
                <span className="ndsi-val mono">{r.filename}</span>
              </div>

              <div className="ndsi-card-row">
                <span className="ndsi-key">
                  {t('ndsi.uploaded', { defaultValue: 'Uploaded' })}:
                </span>
                <span className="ndsi-val">
                  {r.created_at ? `${timeAgo(r.created_at, t)} (${new Date(r.created_at).toLocaleString(locale)})` : '—'}
                </span>
              </div>

              <div className="ndsi-actions">
                <button
                  type="button"
                  className="ndsi-btn"
                  onClick={() => resolveAndGoToPatient(r)}
                  title={t('ndsi.viewPatient', {
                    defaultValue: 'Open patient Reports tab',
                  })}
                >
                  {t('ndsi.viewPatient', { defaultValue: 'View Patient' })}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
